import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Svg, SvgBubble } from '@hopskipdrive/react-components';
import { analyticsRiderServicesFieldChanged } from '@rideiq/api/services/Analytics';
import { MIN_CAR_SEAT_AGE } from '@rideiq/riders/constants/riderServices';
import { OptionCard } from '@rideiq/riders/features/RiderForm/components/OptionCard';
import { differenceInYears } from 'date-fns';
import styled from 'styled-components';

import { useLiveServices } from '../../../hooks/useLiveServices';
import type { RiderFormValues } from '../../../types';
import { parseServiceConflicts } from '../../../utils/parseServiceConflicts';

import { SubsectionHeaderRow, SubsectionKbLink, SubsectionLabel, SubsectionWrapper } from './styles';

const KB_URL = 'https://help.hopskipdrive.com/hc/en-us/articles/44647796236436-Rider-Services';

const isRecordingService = (name: string) => name.startsWith('Ride Recording:');

export const InRideServicesSubsection = () => {
  const { setValue, control } = useFormContext<RiderFormValues>();
  const services = useWatch({ control, name: 'services' }) ?? [];
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });
  const riderAge = dateOfBirth ? differenceInYears(Date.now(), new Date(dateOfBirth)) : undefined;
  const liveServicesState = useLiveServices();
  const serviceConflictMessages = useWatch({ control, name: 'serviceConflictMessages' }) ?? [];
  const conflictsByName = parseServiceConflicts(serviceConflictMessages);
  const { slug } = useParams<{ slug?: string }>();
  const liveServices =
    liveServicesState.status === 'ready'
      ? liveServicesState.services
          .filter((s) => !isRecordingService(s.name) && s.available)
          .sort((a, b) => a.name.localeCompare(b.name))
      : [];

  const toggleService = (code: string, next: boolean) => {
    const without = services.filter((c) => c !== code);
    const updated = next ? [...without, code] : without;

    setValue('services', updated, { shouldDirty: true });
    if (code === 'CS' && next) {
      setValue('boosterSeat', false, { shouldDirty: true });
    }
    analyticsRiderServicesFieldChanged({ field_name: code, new_value: next, rider_id: slug ?? null });
  };

  return (
    <SubsectionWrapper data-testid="rider-services-subsection">
      <SubsectionHeaderRow>
        <SubsectionLabel>Rider services</SubsectionLabel>
        <SubsectionKbLink href={KB_URL} target="_blank" rel="noopener noreferrer" aria-label="Rider services help">
          <SvgBubble>
            <Svg icon="question" />
          </SvgBubble>
        </SubsectionKbLink>
      </SubsectionHeaderRow>
      <CardGrid>
        {liveServices.map((service) => {
          const conflictingNames = conflictsByName[service.name];

          return (
            <OptionCard
              key={service.code}
              label={service.name}
              tooltip={service.detail}
              checked={services.includes(service.code)}
              onChange={(next) => toggleService(service.code, next)}
              disabled={service.code === 'CS' && riderAge !== undefined && riderAge < MIN_CAR_SEAT_AGE}
              error={Boolean(conflictingNames?.length)}
              errorMessage={conflictingNames?.length ? `Conflicts with ${conflictingNames.join(', ')}` : undefined}
            />
          );
        })}
      </CardGrid>
    </SubsectionWrapper>
  );
};

const CardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
